import { Trans, useTranslation } from 'react-i18next';
import React, { useState, useCallback, useRef, useMemo } from 'react';
import cx from 'clsx';
import Checkbox from 'antd/es/checkbox';
import { useHistory } from 'react-router-dom';

import { IconRcHiBackButton, IconRcHiOfferPriceDescription } from '@/assets/hi';
import { Modal, Button, HelperTooltip } from '@/components';
import { AccountStatus } from '@/consts';
import { useCurrentAddressInfo, useCurrentAddressAccount, useL2AccountStatus } from '@/hooks';
import {
  getSignedTransferMessage,
  getTransferMsgRawTextForUser,
  devLog,
  getUserNameByUserInfo,
  numFormat,
} from '@/utils';
import { PasswordInput, PasswordErrorType } from '@/views/Account/components';

import { useHiFreeModal } from '../../hooks/useHiFreeModal';
import { formatOfferPriceNumber } from '../../utils';
import style from './index.module.css';
import './index.scss';

const { CommonModal } = Modal;

type OfferModalProps = {
  turnOff(): void;
  price?: number;
  submitOffer: (tx?: any) => Promise<boolean>;
  otherSideUserCardInfo: any;
  isFree?: boolean;
  freeMessageCount?: number;
};

export const OfferModal = ({
  turnOff,
  price,
  submitOffer,
  isFree,
  otherSideUserCardInfo,
  freeMessageCount = 0,
}: OfferModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  // const [isLoading, setIsLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { address, account: currentAccountInfo } = useCurrentAddressInfo();
  const [page, setPage] = useState(0);
  const [enableSkipPassword, setSkipPassword] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [passwordErrorType, setPasswordErrorType] = useState<PasswordErrorType>();
  const [isSending, setIsSending] = useState(false);
  const {
    userInfo: addrInfo,
    unchargedOfferCount,
    initialPrice,
    offerPrice,
  } = otherSideUserCardInfo;
  const otherSideAccountName = useMemo(() => getUserNameByUserInfo(addrInfo), [addrInfo]);
  const { hideHiFreeModal, setHideHiFreeModal } = useHiFreeModal();
  const { data: myAccountInfo, ...myAccountInfoQuery } = useCurrentAddressAccount();
  const status = useL2AccountStatus();

  const insufficientBalance = useMemo(
    () => !isFree && (myAccountInfo?.balance || 0) * 10000 < offerPrice,
    [isFree, myAccountInfo?.balance, offerPrice],
  );

  const isSkipPassword = useMemo(
    () => true || (!myAccountInfoQuery.isLoading && myAccountInfo?.free_password),
    [myAccountInfo?.free_password, myAccountInfoQuery.isLoading],
  );

  const showKeyError = useMemo(
    () => !isSkipPassword && status !== AccountStatus.NORMAL,
    [isSkipPassword, status],
  );

  const onPasswordChange = useCallback(() => {
    setIsButtonDisabled(passwordInputRef?.current?.value?.length !== 6);
  }, []);

  const onSubmitOffer = useCallback(async () => {
    const secret = passwordInputRef?.current?.value;
    const nextTxNonce = myAccountInfo?.nonce || 0;

    if (
      !price ||
      (!secret && !isSkipPassword) ||
      myAccountInfoQuery.isLoading ||
      !address ||
      !addrInfo?.id ||
      isSending
    ) {
      return;
    }

    setIsSending(true);
    try {
      let tx;
      try {
        tx = getSignedTransferMessage(
          address,
          addrInfo?.id,
          price,
          nextTxNonce,
          currentAccountInfo?.encryptedPrivateKey,
          secret,
          enableSkipPassword,
        );
      } catch (e) {
        setPasswordErrorType(PasswordErrorType.WRONG_PASSWORD);
        setIsSending(false);
        return;
      }

      devLog('SubmitOffer', 'tx', tx);
      await submitOffer(tx).then((success: boolean) => {
        setIsSending(false);

        if (success) {
          turnOff();
        } else {
          setPage(0);
        }
      });
    } catch (errStr) {
      console.log('SubmitOffer error', errStr);
      if (passwordInputRef.current) {
        passwordInputRef.current.value = '';
      }
      setPasswordErrorType(PasswordErrorType.VERIFY_ERROR);
      setIsSending(false);
    }
  }, [
    addrInfo?.id,
    address,
    currentAccountInfo?.encryptedPrivateKey,
    enableSkipPassword,
    isSending,
    isSkipPassword,
    myAccountInfo?.nonce,
    myAccountInfoQuery.isLoading,
    price,
    submitOffer,
    turnOff,
  ]);

  const onClickSend = useCallback(() => {
    if (isFree) {
      submitOffer();
      turnOff();
    } else {
      setPasswordErrorType(undefined);
      setPage(1);
    }
  }, [isFree, submitOffer, turnOff]);

  const transferMsg = useMemo(() => {
    return (
      <div className={style.transferMsg}>
        <div>
          <Trans i18nKey="OfferModal.1f92buu" values={{ price: `${numFormat(price, 2)} USDC` }}>
            Pay
            <span className={style.strong}>USDC</span>
            to send a message to
          </Trans>
        </div>
        <span className={style.strong}>{addrInfo?.id}</span>
      </div>
    );
  }, [addrInfo?.id, price]);

  const rawText = useMemo(
    () =>
      address &&
      getTransferMsgRawTextForUser(address, addrInfo?.id, price!, myAccountInfo?.nonce || 0),
    [addrInfo?.id, address, myAccountInfo?.nonce, price],
  );

  const navL2Page = useCallback(() => {
    turnOff();
    history.push('/account');
  }, [turnOff, history]);

  return (
    <CommonModal onRequestClose={turnOff} size="medium">
      <div className={cx(style.container)}>
        {page === 0 ? (
          <div className={style.review}>
            <div className={style.header}>
              <span className={style.title}>
                {t('OfferModal.l1iz94', { name: otherSideAccountName })}
              </span>
            </div>

            <div className={style.offerPrice}>
              ${isFree ? 0 : formatOfferPriceNumber(offerPrice)}
              {isFree ? (
                <span className={style.freePrice}>{formatOfferPriceNumber(offerPrice, '$')}</span>
              ) : null}
              <HelperTooltip title={<div>{t('OfferModal.1gdcztm')}</div>} placement={'right'}>
                <IconRcHiOfferPriceDescription className={style.icon} />
              </HelperTooltip>
            </div>
            <div className={style.offerCount}>
              {t('OfferModal.c9o3pp', {
                name: otherSideAccountName,
                amount: unchargedOfferCount || 0,
              })}
              <div className={style.basePrice}>
                {t('OfferModal.1u3bquo', {
                  price: formatOfferPriceNumber(initialPrice, '$'),
                  name: otherSideAccountName,
                })}
              </div>
            </div>
            <div className={style.insufficientBalance}>
              {insufficientBalance ? (
                <>
                  {t('OfferModal.31j7iz')}{' '}
                  <span className={style.balance}>${myAccountInfo?.balance}</span>
                </>
              ) : null}
            </div>
            <div className={style.footer}>
              <Button className={cx(style.footerButton, style.cancelBtn)} onClick={turnOff}>
                {t('OfferModal.x9d2fu')}
              </Button>
              {insufficientBalance ? (
                <Button type="primary" className={style.footerButton} onClick={navL2Page}>
                  {t('L2PayModal.u5swep')}
                </Button>
              ) : (
                <Button
                  type="primary"
                  className={cx(style.footerButton, isFree ? style.isFreeButton : null)}
                  onClick={onClickSend}>
                  {isFree ? t('OfferModal.send-free') : t('OfferModal.paySend')}
                </Button>
              )}
            </div>
            {isFree ? (
              freeMessageCount > 0 ? (
                <span className={style.footerText}>
                  <Trans i18nKey="OfferModal.15icq3v" values={{ amount: freeMessageCount }}>
                    You can reply
                    <span className={style.freeMessageCountText}>{freeMessageCount}</span>messages
                    to him/her for free since he/she has sent you a message.
                  </Trans>
                </span>
              ) : (
                <div className={style.footerText}>
                  <Trans i18nKey="OfferModal.bwpvks" values={{ name: otherSideAccountName }}>
                    {otherSideAccountName}
                    is following you,
                    <span className={style.free}>you don't need to pay</span>
                  </Trans>
                </div>
              )
            ) : (
              <div className={style.footerText}>{t('OfferModal.1wrv960')}</div>
            )}
            {isFree ? (
              <div
                className={style.optionBottom}
                onClick={() => setHideHiFreeModal(!hideHiFreeModal)}>
                <Checkbox className={style.checkbox} checked={hideHiFreeModal} />
                {t('OfferModal.y1snuf')}
              </div>
            ) : null}
          </div>
        ) : null}

        {page === 1 ? (
          <div className={style.confirm}>
            <div className={style.header}>
              <IconRcHiBackButton className={style.back} onClick={() => setPage(0)} />
              <span className={style.title}>{t('OfferModal.kttpqq')}</span>

              <HelperTooltip
                overlayClassName="offer-modal-raw-text-tooltip"
                title={rawText}
                placement={'rightTop'}>
                <div className={style.viewRaw}>{t('OfferModal.jr23al')}</div>
              </HelperTooltip>
            </div>
            <>
              <div className={cx(isSkipPassword && style.isSkipPassword)}>{transferMsg}</div>
              {isSkipPassword ? null : (
                <div className={style.passwordWrap}>
                  <PasswordInput
                    passwordInputRef={passwordInputRef}
                    onPasswordChange={onPasswordChange}
                    setSkipPassword={setSkipPassword}
                    showError={!!passwordErrorType}
                    errorType={passwordErrorType}
                  />
                </div>
              )}
              <div className={style.accountBalance}>
                {t('L2PayModal.1j0yn7m')} {numFormat(myAccountInfo?.balance, 2, '$')}
              </div>

              <div className={style.footer}>
                <Button className={cx(style.footerButton, style.cancelBtn)} onClick={turnOff}>
                  {t('OfferModal.x9d2fu')}
                </Button>
                <Button
                  type="primary"
                  loading={isSending}
                  disabled={showKeyError || (isButtonDisabled && !isSkipPassword)}
                  className={style.footerButton}
                  onClick={onSubmitOffer}>
                  {t('OfferModal.179d1nk')}
                </Button>
              </div>
            </>
          </div>
        ) : null}
      </div>
    </CommonModal>
  );
};
